// Balance «Como se tomó» de un DNG, igual que Adobe (DNG SDK): AsShotNeutral (o AsShotWhiteXY)
// + ColorMatrix1/2 interpoladas por temperatura → xy → temperatura/tinte (Robertson).
// CR3/NEF/ARW no guardan matrices de color en el archivo → null (solo el plugin corrige su WB).

// Tabla de Robertson (r = mired, u, v, pendiente t) — dng_temperature.cpp.
const T = [
  [0, 0.18006, 0.26352, -0.24341], [10, 0.18066, 0.26589, -0.25479], [20, 0.18133, 0.26846, -0.26876],
  [30, 0.18208, 0.27119, -0.28539], [40, 0.18293, 0.27407, -0.30470], [50, 0.18388, 0.27709, -0.32675],
  [60, 0.18494, 0.28021, -0.35156], [70, 0.18611, 0.28342, -0.37915], [80, 0.18740, 0.28668, -0.40955],
  [90, 0.18880, 0.28997, -0.44278], [100, 0.19032, 0.29326, -0.47888], [125, 0.19462, 0.30141, -0.58204],
  [150, 0.19962, 0.30921, -0.70471], [175, 0.20525, 0.31647, -0.84901], [200, 0.21142, 0.32312, -1.0182],
  [225, 0.21807, 0.32909, -1.2168], [250, 0.22511, 0.33439, -1.4512], [275, 0.23247, 0.33904, -1.7298],
  [300, 0.24010, 0.34308, -2.0637], [325, 0.24702, 0.34655, -2.4681], [350, 0.25591, 0.34951, -2.9641],
  [375, 0.26400, 0.35200, -3.5814], [400, 0.27218, 0.35407, -4.3633], [425, 0.28039, 0.35577, -5.3762],
  [450, 0.28863, 0.35714, -6.7262], [475, 0.29685, 0.35823, -8.5955], [500, 0.30505, 0.35907, -11.324],
  [525, 0.31320, 0.35968, -15.628], [550, 0.32129, 0.36011, -23.325], [575, 0.32931, 0.36038, -40.770],
  [600, 0.33724, 0.36051, -116.45],
];
const TINT_SCALE = -3000;
const D50 = [0.3457, 0.3585];
// CalibrationIlluminant (EXIF LightSource) → temperatura aproximada (K).
const ILLUM_TEMP = { 1: 5500, 2: 3800, 3: 2850, 4: 5500, 9: 5500, 10: 6500, 11: 7500, 12: 6430, 13: 5000, 14: 4150,
  15: 3525, 17: 2850, 18: 4875, 19: 6775, 20: 5500, 21: 6500, 22: 7500, 23: 5000, 24: 3200 };

export function xyToTempTint([x, y]) {
  const u = (2 * x) / (1.5 - x + 6 * y), v = (3 * y) / (1.5 - x + 6 * y);
  let lastDt = 0, lastDu = 0, lastDv = 0;
  for (let i = 1; i <= 30; i++) {
    let du = 1, dv = T[i][3];
    const len = Math.hypot(du, dv); du /= len; dv /= len;
    let uu = u - T[i][1], vv = v - T[i][2];
    let dt = -uu * dv + vv * du;
    if (dt <= 0 || i === 30) {
      if (dt > 0) dt = 0;
      dt = -dt;
      const f = i === 1 ? 0 : dt / (lastDt + dt);
      const temperature = 1e6 / (T[i - 1][0] * f + T[i][0] * (1 - f));
      uu = u - (T[i - 1][1] * f + T[i][1] * (1 - f));
      vv = v - (T[i - 1][2] * f + T[i][2] * (1 - f));
      du = du * (1 - f) + lastDu * f; dv = dv * (1 - f) + lastDv * f;
      const l2 = Math.hypot(du, dv); du /= l2; dv /= l2;
      return { temperature, tint: (uu * du + vv * dv) * TINT_SCALE };
    }
    lastDt = dt; lastDu = du; lastDv = dv;
  }
  return null;
}

function inv3(m) {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (!det) return null;
  return [A / det, -(b * i - c * h) / det, (b * f - c * e) / det, B / det, (a * i - c * g) / det,
    -(a * f - c * d) / det, C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
}
const mul = (m, v) => [0, 1, 2].map((r) => m[r * 3] * v[0] + m[r * 3 + 1] * v[1] + m[r * 3 + 2] * v[2]);

function xyzToCamera(xy, cal) {
  if (!cal.cm2) return cal.cm1;
  let [t1, t2, m1, m2] = [cal.t1, cal.t2, cal.cm1, cal.cm2];
  if (t1 > t2) [t1, t2, m1, m2] = [t2, t1, m2, m1];
  const temp = xyToTempTint(xy)?.temperature ?? 5000;
  const g = temp <= t1 ? 1 : temp >= t2 ? 0 : (1 / temp - 1 / t2) / (1 / t1 - 1 / t2);
  return m1.map((v, k) => g * v + (1 - g) * m2[k]);
}

// cal: { cm1, cm2?, t1, t2 }, neutral: [r,g,b] (cámara) → xy (DNG SDK NeutralToXY).
export function neutralToXy(neutral, cal) {
  let last = D50;
  for (let pass = 0; pass < 30; pass++) {
    const inv = inv3(xyzToCamera(last, cal));
    if (!inv) return null;
    const xyz = mul(inv, neutral);
    const s = xyz[0] + xyz[1] + xyz[2];
    if (!(s > 0)) return null;
    const next = [xyz[0] / s, xyz[1] / s];
    if (Math.abs(next[0] - last[0]) + Math.abs(next[1] - last[1]) < 1e-7) return next;
    last = pass === 29 ? [(last[0] + next[0]) / 2, (last[1] + next[1]) / 2] : next;
  }
  return last;
}

// ---- Lectura de etiquetas DNG (IFD0) ----
const u16 = (b, o, le) => (le ? b[o] | (b[o + 1] << 8) : (b[o] << 8) | b[o + 1]);
const u32 = (b, o, le) => (le ? (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0
  : ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0);
const i32 = (b, o, le) => u32(b, o, le) | 0;

export function readDngWhiteBalance(bytes) {
  const b = bytes;
  if (b.length < 16) return null;
  const le = b[0] === 0x49 && b[1] === 0x49;
  if (!le && !(b[0] === 0x4d && b[1] === 0x4d)) return null;
  const ifd = u32(b, 4, le);
  if (ifd + 2 > b.length) return null;
  const n = u16(b, ifd, le);
  const tags = {};
  for (let k = 0; k < n && k < 1000; k++) {
    const o = ifd + 2 + k * 12;
    if (o + 12 > b.length) break;
    const tag = u16(b, o, le), type = u16(b, o + 2, le), count = u32(b, o + 4, le);
    if (![0xc621, 0xc622, 0xc628, 0xc629, 0xc65a, 0xc65b].includes(tag)) continue;
    if (type === 3) { tags[tag] = [u16(b, o + 8, le)]; continue; } // SHORT (iluminante)
    if ((type !== 5 && type !== 10) || count > 9) continue;          // RATIONAL / SRATIONAL
    const at = u32(b, o + 8, le);
    if (at + count * 8 > b.length) return null;
    const vals = [];
    for (let j = 0; j < count; j++) {
      const num = type === 10 ? i32(b, at + j * 8, le) : u32(b, at + j * 8, le);
      const den = type === 10 ? i32(b, at + j * 8 + 4, le) : u32(b, at + j * 8 + 4, le);
      vals.push(den ? num / den : 0);
    }
    tags[tag] = vals;
  }
  const cm1 = tags[0xc621];
  if (!cm1 || cm1.length !== 9) return null;
  const cal = { cm1, cm2: tags[0xc622]?.length === 9 ? tags[0xc622] : null,
    t1: ILLUM_TEMP[tags[0xc65a]?.[0]] || 2850, t2: ILLUM_TEMP[tags[0xc65b]?.[0]] || 6500 };
  let xy = null;
  if (tags[0xc629]?.length === 2) xy = tags[0xc629];
  else if (tags[0xc628]?.length === 3 && tags[0xc628].every((v) => v > 0)) xy = neutralToXy(tags[0xc628], cal);
  const r = xy && xyToTempTint(xy);
  if (!r || !Number.isFinite(r.temperature) || r.temperature < 2000 || r.temperature > 50000) return null;
  return { temperature: Math.round(r.temperature), tint: Math.round(r.tint) };
}

export async function readAsShotFromFile(file) {
  if (!file || !/\.dng$/i.test(file.name || "")) return null;
  try {
    return readDngWhiteBalance(new Uint8Array(await file.slice(0, Math.min(file.size, 2 * 1024 * 1024)).arrayBuffer()));
  } catch {
    return null;
  }
}
